import {
  BUILDING_DOCUMENT_CATEGORIES,
  BUILDING_DOCUMENT_CATEGORY_LABELS,
  type BuildingDocumentCategory,
} from '@sitewise/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  LoadingState,
  cn,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Download, FileText, FolderOpen, Trash2, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import {
  confirmBuildingDocument,
  deleteBuildingDocument,
  fetchBuildingDocumentUrl,
  listBuildingDocuments,
  presignBuildingDocument,
  type BuildingDocument,
} from '../../../api/buildings.js';
import { ApiError } from '../../../lib/api-client.js';

const MAX_BYTES = 50 * 1024 * 1024;
const CURRENT_YEAR = new Date().getFullYear();

interface Props {
  buildingId: string;
}

/**
 * Building Records — filing cabinet of permits / yearly certificates.
 * Category chips → year folders → files.
 */
export function BuildingRecordsTab({ buildingId }: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [category, setCategory] = useState<BuildingDocumentCategory>('boiler');
  const [uploadYear, setUploadYear] = useState(CURRENT_YEAR);
  const [expandedYears, setExpandedYears] = useState<Set<number>>(
    () => new Set([CURRENT_YEAR]),
  );
  const [uploadingName, setUploadingName] = useState<string | null>(null);

  const docsQuery = useQuery({
    queryKey: ['building', buildingId, 'documents'],
    queryFn: () => listBuildingDocuments(buildingId),
  });

  const allDocs = docsQuery.data?.documents ?? [];

  const countsByCategory = useMemo(() => {
    const map = new Map<BuildingDocumentCategory, number>();
    for (const c of BUILDING_DOCUMENT_CATEGORIES) map.set(c, 0);
    for (const d of allDocs) {
      map.set(d.category, (map.get(d.category) ?? 0) + 1);
    }
    return map;
  }, [allDocs]);

  const categoryDocs = useMemo(
    () => allDocs.filter((d) => d.category === category),
    [allDocs, category],
  );

  const years = useMemo(() => {
    const set = new Set<number>(categoryDocs.map((d) => d.year));
    // Always offer the upload year so the user can drop into an empty folder.
    set.add(uploadYear);
    return Array.from(set).sort((a, b) => b - a);
  }, [categoryDocs, uploadYear]);

  const docsByYear = useMemo(() => {
    const map = new Map<number, BuildingDocument[]>();
    for (const y of years) map.set(y, []);
    for (const d of categoryDocs) {
      const list = map.get(d.year) ?? [];
      list.push(d);
      map.set(d.year, list);
    }
    return map;
  }, [categoryDocs, years]);

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => deleteBuildingDocument(buildingId, documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['building', buildingId, 'documents'],
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive document');
    },
  });

  function toggleYear(year: number) {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  }

  async function handleUpload(file: File) {
    if (file.size > MAX_BYTES) {
      toast.error('File is larger than 50 MB.');
      return;
    }
    setUploadingName(file.name);
    try {
      const presigned = await presignBuildingDocument(buildingId, {
        category,
        year: uploadYear,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        caption: null,
      });

      const uploadResponse = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: presigned.headers,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload to storage failed (${uploadResponse.status})`);
      }

      await confirmBuildingDocument(buildingId, presigned.document.id);
      setExpandedYears((prev) => new Set(prev).add(uploadYear));
      toast.success('Document uploaded.');
      void queryClient.invalidateQueries({
        queryKey: ['building', buildingId, 'documents'],
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingName(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function openSigned(docId: string) {
    try {
      const signed = await fetchBuildingDocumentUrl(buildingId, docId);
      window.open(signed.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not download');
    }
  }

  const yearOptions = useMemo(() => {
    const opts: number[] = [];
    for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 30; y -= 1) opts.push(y);
    return opts;
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-fg-muted">
          Permits and yearly certificates. Old years stay — nothing overwrites prior filings.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {BUILDING_DOCUMENT_CATEGORIES.map((c) => {
          const count = countsByCategory.get(c) ?? 0;
          const active = category === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'border-fg-secondary bg-surface-muted text-fg'
                  : 'border-border bg-surface text-fg-secondary hover:border-fg-muted',
              )}
            >
              {BUILDING_DOCUMENT_CATEGORY_LABELS[c]}
              <span
                className={cn(
                  'rounded px-1.5 text-xs tabular-nums',
                  active ? 'bg-fg/10' : 'bg-surface-muted',
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <label className="text-xs font-medium text-fg-muted">
            Year
            <select
              className="ml-2 rounded border border-border bg-surface px-2 py-1 text-sm"
              value={uploadYear}
              onChange={(e) => setUploadYear(Number.parseInt(e.target.value, 10))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUpload(file);
            }}
          />
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={Boolean(uploadingName)}
          >
            <Upload className="h-3.5 w-3.5" />
            {uploadingName
              ? `Uploading ${uploadingName}…`
              : `Upload to ${BUILDING_DOCUMENT_CATEGORY_LABELS[category]} · ${uploadYear}`}
          </Button>
          <span className="text-xs text-fg-muted">Max 50 MB · PDF or image</span>
        </CardContent>
      </Card>

      {docsQuery.isLoading ? (
        <LoadingState rows={4} />
      ) : categoryDocs.length === 0 && !years.includes(uploadYear) ? (
        <EmptyState
          icon={FolderOpen}
          title={`No ${BUILDING_DOCUMENT_CATEGORY_LABELS[category].toLowerCase()} records`}
          description="Upload a certificate or permit for a filing year to start this archive."
        />
      ) : (
        <div className="space-y-2">
          {years.map((year) => {
            const docs = docsByYear.get(year) ?? [];
            const isOpen = expandedYears.has(year);
            // Hide empty years except the selected upload year so the UI
            // doesn't list 30 blank folders.
            if (docs.length === 0 && year !== uploadYear) return null;
            return (
              <div
                key={year}
                className="overflow-hidden rounded-lg border border-border bg-surface"
              >
                <button
                  type="button"
                  onClick={() => toggleYear(year)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-muted"
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-fg-muted" />
                    <span className="font-semibold tabular-nums">{year}</span>
                    <span className="text-xs text-fg-muted">
                      {docs.length} file{docs.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-fg-muted transition-transform',
                      isOpen ? 'rotate-0' : '-rotate-90',
                    )}
                  />
                </button>
                {isOpen ? (
                  <div className="border-t border-border">
                    {docs.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-fg-muted">
                        No files in {year} yet. Use Upload above to add one.
                      </p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {docs.map((doc) => (
                          <DocumentRow
                            key={doc.id}
                            doc={doc}
                            onDownload={() => openSigned(doc.id)}
                            onDelete={() => {
                              if (confirm(`Archive ${doc.originalFilename}?`)) {
                                deleteMutation.mutate(doc.id);
                              }
                            }}
                            deleting={deleteMutation.isPending}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DocumentRow({
  doc,
  onDownload,
  onDelete,
  deleting,
}: {
  doc: BuildingDocument;
  onDownload: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
      <div className="flex min-w-0 items-center gap-3">
        <FileText className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
        <div className="min-w-0">
          <div className="truncate font-medium">{doc.originalFilename}</div>
          <div className="text-xs text-fg-muted">
            <Badge variant="outline" className="mr-1.5 text-xs">
              {BUILDING_DOCUMENT_CATEGORY_LABELS[doc.category]}
            </Badge>
            {formatBytes(doc.bytes)} · {new Date(doc.createdAt).toLocaleDateString()}
          </div>
          {doc.caption ? (
            <div className="truncate text-xs text-fg-secondary">{doc.caption}</div>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="icon" aria-label="Download" onClick={onDownload}>
          <Download className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Archive document"
          disabled={deleting}
          className="text-danger hover:text-danger"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
